// Visual carrier-tracking progress for one leg (outbound or return) of a
// Shipping/DoorDash rental. Mirrors PickupTracker.tsx's STATUS_STEPS stepper
// pattern. See FULFILLMENT_PLAN.md #19.5.

// Shippo has no carrier auto-detect -- the token is required and must match
// one of Shippo's carrier identifiers exactly.
export const CARRIER_OPTIONS = [
  { value: 'usps', label: 'USPS' },
  { value: 'ups', label: 'UPS' },
  { value: 'fedex', label: 'FedEx' },
  { value: 'dhl_express', label: 'DHL Express' },
  { value: 'ontrac', label: 'OnTrac' },
]

export interface ShipmentTrackingData {
  id: string
  carrier: string | null
  trackingNumber: string
  status: string
  statusDetail: string | null
  estDeliveryDate: string | null
  publicUrl: string | null
  events: Array<{ status: string; message: string; datetime: string; location: string | null }>
  lastCheckedAt: string | null
}

const STEPS = [
  { status: 'PRE_TRANSIT', label: 'Label Created', icon: 'mdi-package-variant-closed' },
  { status: 'IN_TRANSIT', label: 'In Transit', icon: 'mdi-truck-fast' },
  { status: 'OUT_FOR_DELIVERY', label: 'Out for Delivery', icon: 'mdi-truck-delivery' },
  { status: 'DELIVERED', label: 'Delivered', icon: 'mdi-check-circle-outline' },
]

const EXCEPTION_STATUS_COPY: Record<string, { label: string; color: string }> = {
  RETURN_TO_SENDER: { label: 'Returned to sender', color: 'bg-amber-50 border-amber-200 text-amber-800' },
  FAILURE: { label: 'Delivery failed', color: 'bg-red-50 border-red-200 text-red-700' },
  CANCELLED: { label: 'Tracking cancelled', color: 'bg-[var(--bg-tint)] border-[var(--border)] text-[var(--text-secondary)]' },
  ERROR: { label: 'Tracking error', color: 'bg-red-50 border-red-200 text-red-700' },
  AVAILABLE_FOR_PICKUP: { label: 'Available for pickup', color: 'bg-blue-50 border-blue-200 text-blue-700' },
  UNKNOWN: { label: 'Status unknown', color: 'bg-[var(--bg-tint)] border-[var(--border)] text-[var(--text-secondary)]' },
}

function formatDateTime(value: string | null) {
  if (!value) return null
  return new Date(value).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  })
}

interface Props {
  title: string
  tracking: ShipmentTrackingData
  onRefresh: () => void
  refreshing: boolean
}

export default function ShipmentTrackingCard({ title, tracking, onRefresh, refreshing }: Props) {
  const currentStep = STEPS.findIndex((s) => s.status === tracking.status)
  const exception = EXCEPTION_STATUS_COPY[tracking.status]

  return (
    <div className="bg-white rounded-xl border border-[var(--border)] p-4 mb-4">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="font-medium text-[var(--text-primary)]">{title}</h3>
          <p className="text-sm text-[var(--text-secondary)]">
            {tracking.carrier ? tracking.carrier.toUpperCase() : 'Carrier not set'} &middot; {tracking.trackingNumber}
          </p>
        </div>
        <button
          onClick={onRefresh}
          disabled={refreshing}
          className="text-sm font-medium text-[var(--forest-green)] hover:underline whitespace-nowrap disabled:opacity-50"
        >
          <i className={`mdi mdi-refresh mr-1 ${refreshing ? 'animate-spin' : ''}`}></i>
          {refreshing ? 'Refreshing...' : 'Refresh status'}
        </button>
      </div>

      {currentStep >= 0 ? (
        <div className="flex items-center justify-between mb-2">
          {STEPS.map((step, index) => (
            <div key={step.status} className="relative flex flex-col items-center flex-1">
              {index < STEPS.length - 1 && (
                // Anchored to each circle's actual outer edge, not its center -- the
                // circle is w-9 (36px, 18px radius), so the line starts 18px right of
                // this column's center (past this circle's rim) and stops 18px short of
                // the far end (before the next circle's rim), sitting purely in the gap
                // between them, rather than relying on the circle's fill to visually
                // mask an underlying line that reaches all the way to its center.
                <div className={`absolute h-0.5 top-[18px] left-[calc(50%+18px)] w-[calc(100%-36px)] ${
                  index < currentStep ? 'bg-[var(--forest-green)]' : 'bg-[var(--border)]'
                }`}></div>
              )}
              <div className={`w-9 h-9 rounded-full flex items-center justify-center ${
                index <= currentStep ? 'bg-[var(--forest-green)] text-white' : 'bg-[var(--bg-tint)] text-[var(--text-muted)]'
              }`}>
                <i className={`mdi ${step.icon} text-base`}></i>
              </div>
              <span className={`text-xs mt-2 text-center ${
                index <= currentStep ? 'text-[var(--text-primary)] font-medium' : 'text-[var(--text-muted)]'
              }`}>
                {step.label}
              </span>
            </div>
          ))}
        </div>
      ) : exception ? (
        <div className={`rounded-lg border px-3 py-2 text-sm mb-2 ${exception.color}`}>
          {exception.label}{tracking.statusDetail ? ` — ${tracking.statusDetail}` : ''}
        </div>
      ) : null}

      {tracking.estDeliveryDate && (
        <p className="text-sm text-[var(--text-secondary)] mt-3">
          Estimated delivery: <span className="font-medium text-[var(--text-primary)]">{formatDateTime(tracking.estDeliveryDate)}</span>
        </p>
      )}

      {tracking.publicUrl && (
        <a
          href={tracking.publicUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm font-medium text-[var(--forest-green)] hover:underline inline-block mt-2"
        >
          View full tracking history <i className="mdi mdi-open-in-new text-xs"></i>
        </a>
      )}

      {tracking.events.length > 0 && (
        <div className="mt-4 pt-4 border-t border-[var(--border)] space-y-3 max-h-48 overflow-y-auto">
          {tracking.events.map((event, i) => (
            <div key={i} className="text-sm">
              <p className="text-[var(--text-primary)]">{event.message}</p>
              <p className="text-xs text-[var(--text-muted)]">
                {formatDateTime(event.datetime)}{event.location ? ` — ${event.location}` : ''}
              </p>
            </div>
          ))}
        </div>
      )}

      {tracking.lastCheckedAt && (
        <p className="text-xs text-[var(--text-muted)] mt-3">
          Last checked {formatDateTime(tracking.lastCheckedAt)}
        </p>
      )}
    </div>
  )
}
